/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from "react";
import { resendVerificationEmail } from "../utils/api";
import { X, Send, Mail } from "lucide-react";
import { useDarkMode } from "../context/DarkModeContext";

interface ResendVerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentEmail: string;
}

const ResendVerificationModal: React.FC<ResendVerificationModalProps> = ({
  isOpen,
  onClose,
  currentEmail,
}) => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState(currentEmail);
  const { isDark } = useDarkMode();

  const handleResend = async () => {
    if (!email.trim()) {
      setMessage("Please enter your email address");
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const response = await resendVerificationEmail(email);
      setMessage(
        response.message || "Verification email sent! Please check your inbox.",
      );

      // Auto-close on success after a delay
      if (response.success) {
        setTimeout(() => {
          onClose();
          setMessage("");
        }, 3000);
      }
    } catch (error: any) {
      setMessage(error.message || "Failed to resend verification email");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setMessage("");
    setEmail(currentEmail);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div
        className={`rounded-xl p-6 max-w-md w-full ${
          isDark ? "bg-gray-800" : "bg-white"
        }`}
      >
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center">
            <Mail className="text-sky-500 mr-3" size={24} />
            <h3 className="text-lg font-semibold text-sky-600">
              Verify Your Email
            </h3>
          </div>
          <button
            onClick={handleClose}
            className={`p-1 rounded-lg transition-colors ${
              isDark ? "hover:bg-gray-700" : "hover:bg-gray-100"
            }`}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="mb-4">
          <p
            className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"} mb-4`}
          >
            Please verify your email address to access all features. We'll send
            a verification link to your email.
          </p>

          <label
            className={`block text-sm font-medium mb-2 ${
              isDark ? "text-gray-300" : "text-gray-700"
            }`}
          >
            Email Address
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent ${
              isDark
                ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                : "border-gray-300 text-gray-900"
            }`}
            placeholder="Enter your email address"
          />
        </div>

        {/* Message */}
        {message && (
          <div
            className={`p-3 rounded-lg mb-4 text-sm ${
              message.includes("Failed") || message.includes("Please enter")
                ? isDark
                  ? "bg-red-900/30 text-red-300 border border-red-700"
                  : "bg-red-100 text-red-800 border border-red-200"
                : isDark
                  ? "bg-green-900/30 text-green-300 border border-green-700"
                  : "bg-green-100 text-green-800 border border-green-200"
            }`}
          >
            {message}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 justify-end">
          <button
            onClick={handleClose}
            disabled={loading}
            className={`px-4 py-2 border rounded-lg font-medium transition-colors ${
              isDark
                ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                : "border-gray-300 text-gray-700 hover:bg-gray-50"
            }`}
          >
            Cancel
          </button>
          <button
            onClick={handleResend}
            disabled={loading}
            className="px-4 py-2 bg-sky-600 text-white rounded-lg font-medium hover:bg-sky-700 disabled:opacity-50 transition-colors flex items-center gap-2"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Sending...
              </>
            ) : (
              <>
                <Send size={16} />
                Send Verification Email
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResendVerificationModal;
